import { Form } from 'antd';
import { Navigate, useNavigate } from 'react-router-dom';

import { register } from '@/apis';
import unionLogo from '@/assets/images/union-logo.png';
import * as AuthComponent from '@/components/Auth';
import BackButton from '@/components/BackButton';
import Logo from '@/components/Logo';
import OutlinedButton from '@/components/OutlinedButton';
import { useAuth } from '@/contexts/AuthContext';
import { useLoading } from '@/contexts/LoadingContext';
import { ButtonHtmlType, Path } from '@/enums';
import { useApiHandler, useBreakpoint } from '@/hooks';

import * as Style from './Register.style';

interface RegisterFormValues {
  name: string;
  email: string;
  password: string;
  confirmPassword: string;
}

const Register = () => {
  const [form] = Form.useForm<RegisterFormValues>();

  const navigate = useNavigate();
  const { handleApiCall } = useApiHandler();
  const { withLoading } = useLoading();
  const { isMobileOrTablet } = useBreakpoint();

  const { token } = useAuth();

  if (token) {
    return <Navigate to={Path.Root} replace />;
  }

  const onFinish = async (values: RegisterFormValues) => {
    await withLoading(async () => {
      const data = await handleApiCall(
        () => register(values.name, values.email, values.password),
        {
          successMessage: '註冊成功！請前往登入頁面',
          context: '註冊帳號',
        },
      );
      if (data) {
        navigate(Path.Login);
      }
    });
  };

  return (
    <Form
      form={form}
      onFinish={onFinish}
      layout="vertical"
      requiredMark={false}
      autoComplete="off"
      style={{ width: '100%' }}
    >
      <Style.FormContainer vertical>
        <Logo
          src={unionLogo}
          alt="Union Logo"
          height="80px"
          style={{ marginBottom: '24px' }}
        />

        <Form.Item
          name="name"
          rules={[
            {
              required: true,
              message: '請輸入用戶姓名',
            },
          ]}
        >
          <AuthComponent.Input
            prefix="用戶姓名 │"
            width="100%"
            height="3.5rem"
          />
        </Form.Item>

        <Form.Item
          name="email"
          rules={[
            {
              required: true,
              message: '請輸入電子信箱',
            },
            {
              type: 'email',
              message: '請輸入有效的電子信箱',
            },
          ]}
        >
          <AuthComponent.Input
            prefix="電子信箱 │"
            type="email"
            autoComplete="email"
            width="100%"
            height="3rem"
          />
        </Form.Item>

        <Form.Item
          name="password"
          rules={[
            {
              required: true,
              message: '請輸入密碼',
            },
          ]}
        >
          <AuthComponent.InputPassword
            prefix="設定密碼 │"
            visibilityToggle={false}
            width="100%"
            height="3.5rem"
          />
        </Form.Item>

        <Form.Item
          name="confirmPassword"
          dependencies={['password']}
          rules={[
            {
              required: true,
              message: '請確認密碼',
            },
            ({ getFieldValue }) => ({
              validator(_, value) {
                if (!value || getFieldValue('password') === value) {
                  return Promise.resolve();
                }
                return Promise.reject(new Error('兩次輸入的密碼不一致'));
              },
            }),
          ]}
        >
          <AuthComponent.InputPassword
            prefix="確認密碼 │"
            visibilityToggle={false}
            width="100%"
            height="3.5rem"
          />
        </Form.Item>

        <Form.Item style={{ alignSelf: 'center' }}>
          <OutlinedButton
            htmlType={ButtonHtmlType.Submit}
            height="4rem"
            width="10rem"
          >
            確認註冊
          </OutlinedButton>
        </Form.Item>

        {isMobileOrTablet && <BackButton to={Path.Login} />}
      </Style.FormContainer>
    </Form>
  );
};

export default Register;
